import type { Shift } from '../types';

/** Unique id for a roster shift. Never key only on day + Date.now() — same-day bulk copy collides. */
export function newRosterShiftId(parts?: {
  day?: string;
  staffId?: number | string | null;
}): string {
  const rand =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
  const day = parts?.day ?? 'd';
  const staff = parts?.staffId != null && parts.staffId !== '' ? String(parts.staffId) : 'x';
  return `shift-${day}-${staff}-${rand}`;
}

/**
 * Copy-from-previous-week used to assign `shift-${day}-${dateTs}-${Date.now()}` inside a tight
 * `.map()`, so every shift on the same day shared one id. Edit then rewrote all of them; delete
 * wiped the whole day. Re-id collisions while keeping the first occurrence of each id.
 */
export function ensureUniqueShiftIds(shifts: Shift[]): { shifts: Shift[]; repaired: number } {
  const seen = new Set<string>();
  let repaired = 0;
  const out = shifts.map((s) => {
    const id = typeof s.id === 'string' ? s.id.trim() : '';
    if (id && !seen.has(id)) {
      seen.add(id);
      return s;
    }
    repaired += 1;
    const nextId = newRosterShiftId({ day: s.day, staffId: s.staffId });
    seen.add(nextId);
    return { ...s, id: nextId };
  });
  return { shifts: out, repaired };
}

/**
 * When saving the roster, the client may have a stale shift list. Shifts that exist on the server
 * but not locally were either (a) added by another device/session, or (b) removed locally since load.
 * We treat (b) as: shift id was in `baselineIds` when the user started from server — do not restore.
 * Anything on the server that is not local and not in baseline is merged in (external adds).
 */
export function mergeExternalRosterShifts(
  localShifts: Shift[],
  serverShifts: Shift[] | null | undefined,
  baselineIds: ReadonlySet<string>
): { merged: Shift[]; externalAdded: Shift[] } {
  // Local list must be unique first — duplicate ids make filter/map edit/delete hit every twin.
  const { shifts: uniqueLocal } = ensureUniqueShiftIds(localShifts);
  if (!serverShifts?.length) {
    return { merged: uniqueLocal, externalAdded: [] };
  }
  const localIds = new Set(uniqueLocal.map((s) => s.id));
  const externalAdded: Shift[] = [];
  for (const s of serverShifts) {
    if (localIds.has(s.id)) continue;
    if (baselineIds.has(s.id)) continue;
    externalAdded.push(s);
  }
  if (externalAdded.length === 0) {
    return { merged: uniqueLocal, externalAdded: [] };
  }
  // External rows can also collide with each other or with local after append.
  const { shifts: merged } = ensureUniqueShiftIds([...uniqueLocal, ...externalAdded]);
  return { merged, externalAdded };
}
